// THE SIGN-OFF ISOMETRIC, as data (owner 2026-11-05: "the same generated isometric
// graphics, split by level, area and system, like the project at a glance, instead of the
// 3D and tiles").
//
// The stage is drawn from TWO sources, in the SAME coordinate space - feet in the model's
// own plan coordinates:
//
//   the tile set   every level's and every area's footprint, from the model export the
//                  sign-off is recorded against (tile.i -> plan x, tile.j -> plan y,
//                  tile.k -> height). Always there, so the stage always draws.
//   the glance     the fine geometry, when the project has a published manifest: one path
//                  per `level|system|zone` bucket, the same ones Project at a Glance draws.
//
// SPLIT BY LEVEL, AREA AND SYSTEM is therefore exact on the first two (they are what the
// sign-off targets are) and as good as the drawings on the third.
//
// Systems come in two vocabularies and the picker shows both, each labelled by where it
// comes from (owner 2026-11-05):
//
//   DRAWN LAYERS    the discipline layers of the drawings - Mechanical, Power, Lighting,
//                   Security, Telecom, Plumbing, Underground, Roof, Process piping, Fire
//                   protection. These are what the isometric draws.
//   IN THE MODEL    the systems the export actually carries, per area, with their element
//                   counts - Conduits, Cable tray, Busway, Gear ... These are what element
//                   coverage is counted from.
//
// A sign-off on a drawn-only layer covers no model elements; it is still recorded, and the
// request says so (utils/signoffRequests.coverNote).

// Each drawn layer, with the glance system id it is drawn from and its colour token.
// `label` is the owner's word for it; `glance` is the id the manifest uses.
export const DRAWN_LAYERS = [
  { id: "ducts", label: "Mechanical", token: "--duct", discipline: "mechanical" },
  { id: "power", label: "Power", token: "--pwr", discipline: "electrical" },
  { id: "lighting", label: "Lighting", token: "--ltg", discipline: "electrical" },
  { id: "security", label: "Security", token: "--sec", discipline: "security" },
  { id: "telecom", label: "Telecom", token: "--tel", discipline: "telecom" },
  { id: "plumbing", label: "Plumbing", token: "--pipe", discipline: "plumbing" },
  { id: "underfloor", label: "Underground", token: "--under", discipline: "plumbing" },
  { id: "processPiping", label: "Process piping", token: "--proc", discipline: "mechanical" },
  { id: "sprinklers", label: "Fire protection", token: "--fire", discipline: "fire" },
  { id: "roof", label: "Roof", token: "--rtutop", discipline: "mechanical" },
];
export const drawnLayer = (id) => DRAWN_LAYERS.find((l) => l.id === id) || null;
/** A drawn layer is picked as "layer:<id>" so it never collides with a model system's name. */
export const LAYER_PREFIX = "layer:";
export const isLayer = (s) => String(s || "").startsWith(LAYER_PREFIX);
export const layerIdOf = (s) => String(s || "").slice(LAYER_PREFIX.length);
export const layerValue = (id) => `${LAYER_PREFIX}${id}`;

/**
 * The levels of the stage, bottom first, from the tile set itself: a level's height is the
 * mean `k` of the tiles that hold its elements, so the stack is in the building's own order
 * without anybody numbering the levels. `names`: the timeline's levelKey -> name.
 */
export function levelsFrom(tiles, names = {}) {
  const sum = new Map();
  for (const t of tiles || []) {
    for (const g of t.g || []) {
      if (!g.l) continue;
      const a = sum.get(g.l) || { k: 0, n: 0, cells: 0 };
      a.k += (t.k || 0) * (g.n || 1);
      a.n += g.n || 1;
      a.cells += 1;
      sum.set(g.l, a);
    }
  }
  return [...sum.entries()]
    .map(([key, a]) => ({ key, name: names[key] || key, height: a.n ? a.k / a.n : 0, tiles: a.cells, elements: a.n }))
    .sort((x, y) => x.height - y.height || String(x.name).localeCompare(String(y.name), undefined, { numeric: true }))
    .map((l, i) => ({ ...l, rank: i + 1 }));
}

/** Every area of a level, with how many of its elements sit on that level. */
export function areasOnLevel(tiles, levelKey) {
  const n = new Map();
  for (const t of tiles || []) {
    for (const g of t.g || []) {
      if (g.l !== levelKey) continue;
      for (const a of [g.a, ...(g.aa || [])]) if (a) n.set(a, (n.get(a) || 0) + (g.n || 0));
    }
  }
  return n;
}

/**
 * The plan cells of one level, as "i,j" -> elements, for the tiles that match.
 * `pick(group)` says whether a group counts; omitted, every placed group on the level does.
 */
export function cellsOn(tiles, levelKey, pick = null) {
  const cells = new Map();
  // no level means no plate: an element the placement could not put on a level is on none
  // of them, and must never be drawn as though it were (isoModel.test.js)
  if (!levelKey) return cells;
  for (const t of tiles || []) {
    let n = 0;
    for (const g of t.g || []) {
      if (g.l !== levelKey) continue;
      if (pick && !pick(g)) continue;
      n += g.n || 0;
    }
    if (n) {
      const k = `${t.i},${t.j}`;
      cells.set(k, (cells.get(k) || 0) + n);
    }
  }
  return cells;
}

export const inArea = (areaId) => (g) => g.a === areaId || (g.aa || []).includes(areaId);
export const hasSystem = (system) => {
  const want = String(system || "").toLowerCase();
  return (g) => (g.s || []).some((x) => String(x).toLowerCase() === want);
};

/** The plan extent, in model units, of some "i,j" cells. */
export function cellBounds(cellKeys, grid) {
  const e = (grid && grid.edge) || 1;
  const ox = (grid && grid.origin && grid.origin.x) || 0;
  const oy = (grid && grid.origin && grid.origin.z) || 0;
  let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity;
  for (const k of cellKeys) {
    const [i, j] = String(k).split(",").map(Number);
    xmin = Math.min(xmin, ox + i * e); xmax = Math.max(xmax, ox + (i + 1) * e);
    ymin = Math.min(ymin, oy + j * e); ymax = Math.max(ymax, oy + (j + 1) * e);
  }
  if (!Number.isFinite(xmin)) return { xmin: 0, ymin: 0, xmax: 1, ymax: 1, w: 1, h: 1 };
  return { xmin, ymin, xmax, ymax, w: Math.max(1, xmax - xmin), h: Math.max(1, ymax - ymin) };
}

/**
 * One SVG path for some "i,j" cells, each cell the projected square of the grid. `project`
 * is the stack projector's (x, y, rank) - the same one the glance buckets are drawn with,
 * so a tile footprint and a drawn pipe land on the same plate.
 */
export function cellsPath(cellKeys, grid, project, rank) {
  const e = (grid && grid.edge) || 1;
  const ox = (grid && grid.origin && grid.origin.x) || 0;
  const oy = (grid && grid.origin && grid.origin.z) || 0;
  const f = (v) => v.toFixed(1);
  let d = "";
  for (const k of cellKeys) {
    const [i, j] = String(k).split(",").map(Number);
    const x0 = ox + i * e; const y0 = oy + j * e;
    const c = [[x0, y0], [x0 + e, y0], [x0 + e, y0 + e], [x0, y0 + e]].map(([x, y]) => project(x, y, rank));
    d += `M${f(c[0][0])} ${f(c[0][1])}L${f(c[1][0])} ${f(c[1][1])}L${f(c[2][0])} ${f(c[2][1])}L${f(c[3][0])} ${f(c[3][1])}Z`;
  }
  return d;
}

/** The centre of some cells on screen, for a label. */
export function cellsCentre(cellKeys, grid, project, rank) {
  const b = cellBounds(cellKeys, grid);
  return project((b.xmin + b.xmax) / 2, (b.ymin + b.ymax) / 2, rank);
}

/**
 * THE SCOPE AS SIGN-OFF TARGETS (owner 2026-11-05: "a user can select 1 or multiple
 * systems, per 1 or multiple areas in 1 or multiple levels").
 *
 * The cross product, collapsed the way the sign-off already reads:
 *   no area picked on a level            -> one `level` target (everything on it)
 *   areas picked, no system              -> one `area` target each
 *   areas and systems picked             -> one `system` target per area x system
 * A system on its own, with no area, cannot be a target: a system sign-off is always a
 * system IN an area (targetKey `system|<areaSetId>|<system>`), which is what it was before
 * the 3D volumes and is what it is again.
 *
 * `levels` HERE IS THE TIMELINE'S OWN MAP, not the names the drawing shows. The server
 * works a level target's key back out of its NAME (utils/elementAreas.levelKey), and the
 * timeline's names are the ones its keys were made from, so they round-trip. A name from
 * somewhere else - the glance manifest calls L2 "Second floor" - does not: it would come
 * back as SECONDFLOOR and the target would cover no tiles. The key is sent as the name
 * when the timeline has none.
 */
export function scopeTargets({ levelKeys = [], areaIds = [], systems = [] }, { areas = {}, levels = {} } = {}) {
  const out = [];
  const seen = new Set();
  const push = (t, k) => { if (!seen.has(k)) { seen.add(k); out.push(t); } };
  const picked = new Set(areaIds);
  const levelsOf = (id) => (areas[id] && areas[id].levelKey) || "";
  // areas picked are grouped under the levels they belong to; a level with no area picked
  // is signed off whole
  const areasByLevel = new Map();
  for (const id of picked) {
    const lk = levelsOf(id);
    if (levelKeys.length && !levelKeys.includes(lk)) continue;
    if (!areasByLevel.has(lk)) areasByLevel.set(lk, []);
    areasByLevel.get(lk).push(id);
  }
  const keys = levelKeys.length ? levelKeys : [...areasByLevel.keys()];
  for (const lk of keys) {
    const mine = areasByLevel.get(lk) || [];
    if (!mine.length) {
      if (!lk) continue;
      push({ kind: "level", levelKey: lk, level: levels[lk] || lk }, `level|${lk}`);
      continue;
    }
    for (const id of mine) {
      if (!systems.length) { push({ kind: "area", areaSetId: id }, `area|${id}`); continue; }
      for (const s of systems) {
        const name = isLayer(s) ? (drawnLayer(layerIdOf(s)) || {}).label || layerIdOf(s) : s;
        push({ kind: "system", areaSetId: id, system: name }, `system|${id}|${String(name).toLowerCase()}`);
      }
    }
  }
  return out;
}

/**
 * The systems to offer for the areas picked, in the two groups the owner asked for.
 * `manifest`: the glance manifest, when the project has one - its drawn systems are the
 * layers that actually have geometry on this project; without one every layer is offered
 * and says it is not drawn yet.
 */
export function systemOptions({ areaIds = [], areas = {}, tiles = [], manifest = null } = {}) {
  const rows = Array.isArray(tiles) ? tiles : (tiles && tiles.tiles) || [];
  const drawnIds = new Set(((manifest && manifest.systems) || []).filter((s) => s.tier != null).map((s) => s.id));
  const layers = DRAWN_LAYERS.map((l) => ({
    value: layerValue(l.id), label: l.label, group: "drawn", token: l.token,
    drawn: drawnIds.has(l.id), note: drawnIds.has(l.id) ? "drawn" : "not in the drawings yet",
  }));
  // the model's own systems, counted over the areas picked (every area when none is)
  const want = new Set(areaIds);
  const counts = new Map();
  for (const t of rows) {
    for (const g of t.g || []) {
      const mine = [g.a, ...(g.aa || [])].filter(Boolean);
      if (want.size && !mine.some((a) => want.has(a))) continue;
      for (const s of g.s || []) counts.set(s, (counts.get(s) || 0) + (g.n || 0));
    }
  }
  // a system a picked area declares but the model does not carry is still offered
  for (const id of want) for (const s of ((areas[id] || {}).systems) || []) if (!counts.has(s)) counts.set(s, 0);
  const model = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, n]) => ({ value: name, label: name, group: "model", elements: n, note: n ? `${n.toLocaleString()} elements` : "none in this export" }));
  return { layers, model };
}
